import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  Paper
} from "@material-ui/core";
import { 
  Event as EventIcon, 
  Schedule as ScheduleIcon, 
  Person as PersonIcon 
} from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import moment from "moment";
import "moment/locale/es";
import ContactSelect from "../ContactSelect";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const AssignContactModal = ({
  open,
  onClose,
  confirmAssignSelectedContact,
  slotToAssign,
  setSelectedContact,
  selectedContact,
}) => {
  const [patientName, setPatientName] = useState("");
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setIsSubmitting(false);
      setPatientName("");
      setPatientSuggestions([]);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedContact) {
        setPatientName("");
        setPatientSuggestions([]);
        return;
    }

    setPatientName(selectedContact.name);
    
    setLoadingPatients(true);
    api.get(`/contacts/${selectedContact.id}/patients`)
      .then(({ data }) => {
        const suggestions = data.map(p => p.patientName || p).filter(Boolean);
        if (!suggestions.includes(selectedContact.name)) {
            suggestions.unshift(selectedContact.name);
        }
        setPatientSuggestions(suggestions);
      })
      .catch(err => toastError(err))
      .finally(() => setLoadingPatients(false));
      
  }, [selectedContact]);

  const handleConfirm = async () => {
    if (!selectedContact || !patientName) return;

    setIsSubmitting(true);
    
    const match = patientSuggestions.find(
        suggestion => suggestion.toLowerCase() === patientName.trim().toLowerCase()
    );
    
    const finalPatientName = match || patientName.trim();

    try {
      await confirmAssignSelectedContact(finalPatientName);
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  };
  
  const dateStr = slotToAssign ? moment(slotToAssign.start).locale("es").format("dddd, D [de] MMMM [de] YYYY") : "";
  const formattedDateStr = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
  const timeStr = slotToAssign ? `${moment(slotToAssign.start).format("HH:mm")} - ${moment(slotToAssign.end).format("HH:mm")} hs` : "";

  return (
    <Dialog open={open} onClose={isSubmitting ? null : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Asignar Cita</DialogTitle>
      
      <DialogContent dividers>
        
        <Paper variant="outlined" style={{ padding: 16, marginBottom: 24, backgroundColor: "#f9f9f9", display: "flex", alignItems: "center" }}>
          <Box mr={2} color="primary.main">
             <EventIcon fontSize="large" />
          </Box>
          <Box>
             <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {formattedDateStr}
             </Typography>
             <Box display="flex" alignItems="center" color="text.secondary">
                <ScheduleIcon fontSize="small" style={{ marginRight: 4 }} />
                <Typography variant="body2">
                   {timeStr}
                </Typography>
             </Box>
          </Box>
        </Paper>
        
        <Box flexDirection="column">
          <Box mb={3}>
            <ContactSelect
              value={selectedContact}
              onChange={(contactObject) => {
                setSelectedContact(contactObject); 
              }}
              returnObject={true}
              disabled={isSubmitting}
            />
          </Box>

          {selectedContact && (
            <Box>
              <Autocomplete
                freeSolo
                options={patientSuggestions}
                inputValue={patientName}
                onInputChange={(event, newInputValue) => {
                  setPatientName(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setPatientName(newValue || ""); 
                }}
                getOptionLabel={(option) => option}
                disabled={isSubmitting}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nombre del Paciente"
                    placeholder="Escribe o selecciona..."
                    variant="outlined"
                    required
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingPatients ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          )}
        </Box>
        
      </DialogContent>
      
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button 
          onClick={onClose} 
          color="default" 
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!selectedContact || !patientName || isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
        >
          {isSubmitting ? "Confirmando..." : "Confirmar Cita"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignContactModal;